import { MessageService } from '../../../services/messages.service';
import { SnackBarService } from '../../../services/snackbar.service';
import { ApplicationValue } from '../../../services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from '../../../services/warmup/warmup.service';
import { TrackingService } from '../../../shared/services/tracking/google-analytics-events.service';
import { SurveyEntityType } from '../../../shared/models/survey/survey-entity-type.enum';
import { TrackingConstants } from './../../../shared/services/tracking/tracking-constants.service';
import { SurveyConfiguration } from '../../../shared/models/survey/survey-config.model';
import { SurveyManagementService } from './../survey-management.service';
import { SurveyTemplate } from '../../../shared/models/survey/survey-template.model';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';


@Component({
  selector: 'add-surveyconfig',
  templateUrl: './add-surveyconfig.component.html',
  styleUrls: ['./add-surveyconfig.component.css']
})

/**
 * Component to add new survey config.
 *
 * @author mohit.khurana@vernuso.com (Mohit Khurana)
 */
export class AddSurveyConfigComponent implements OnInit {
  selectedConfiguration: SurveyConfiguration;
  action: string;
  createSurveyConfigForm: FormGroup;
  entityTypes: SurveyEntityType[];
  surveyTemplates: SurveyTemplate[];
  surveyLocations: ApplicationValue[];
  actionType: string;
  surveyId: number;

  formErrors = {
    'entityType': '',
    'surveyLocation': '',
    'surveyName': '',
    'surveyTabTitle': '',
  };

  validationMessages = {
    'entityType': {
      'required': 'Required field value.',
    },
    'surveyLocation': {
      'required': 'Required field value.',
    },
    'surveyName': {
      'required': 'Required field value.',
    },
    'surveyTabTitle': {
      'required': 'Required field value.',
    }
  };

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MdDialogRef<any>,
    private surveyManagementService: SurveyManagementService,
    private warmUpService: WarmUpService,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private trackingService: TrackingService) { }

  ngOnInit(): void {
    this.buildForm();
    this.setUpEntityTypes();
    this.setUpSurveyName();
    this.setUpSurveyLocation();
    this.updateSurveyConfigForm();
    this.actionType = this.action == 'Add' && 'New' || 'Edit';
  }

  setUpEntityTypes() {
    this.surveyManagementService.getEntityTypes()
      .subscribe(response => {
        if (response.flagResult) {
          this.entityTypes = response.arrResult;
        }
      });
  }

  setUpSurveyName() {
    this.surveyTemplates = this.surveyManagementService.getSurveyTemplatesFromCache();
  }

  setUpSurveyLocation() {
    const surveyLocations =
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.SURVEY_LOCATION);
    if (surveyLocations instanceof Observable) {
      surveyLocations.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.surveyLocations = response.values;
        }
      );
    } else {
      this.surveyLocations = surveyLocations;
    }
  }

  buildForm() {
    this.createSurveyConfigForm = this.formBuilder.group({
      entityType: ['', Validators.required],
      surveyName: ['', Validators.required],
      surveyTabTitle: ['', Validators.required],
      surveyLocation: ['', Validators.required],
    });

    this.createSurveyConfigForm.valueChanges.subscribe(
      data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  onValueChanged(data?: any) {
    if (!this.createSurveyConfigForm) {
      return;
    }
    const form = this.createSurveyConfigForm;

    for (const field in this.formErrors) {
      this.formErrors[field] = '';
      const control = form.get(field);
      if (control && control.touched && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onSubmit({ value, valid }): void {
    const surveyConfiguration = new SurveyConfiguration();
    surveyConfiguration.id = this.surveyId;
    surveyConfiguration.entityType = this.getEntityTypeObject(value.entityType);
    surveyConfiguration.locationId = value.surveyLocation;
    surveyConfiguration.surveyTemplateName = this.getSurveyNameValueObject(value.surveyName).title;
    surveyConfiguration.surveyTemplateVO = this.getSurveyNameValueObject(value.surveyName);
    surveyConfiguration.tabTitle = value.surveyTabTitle;

    this.surveyManagementService.addSurveyConfig(surveyConfiguration)
      .subscribe(response => {
        if (response.flagResult) {
          this.dialogRef.close(response.arrResult);
        }
        this.trackingService
          .emitEvent(
          TrackingConstants.SURVEY_MANAGEMENT,
          TrackingConstants.SAVE_SURVEY_CONFIG,
          TrackingConstants.SUCCESS, 1);

        this.callSnackBarService(response, this.action);
      }, err => {
        this.trackingService
          .emitEvent(
          TrackingConstants.SURVEY_MANAGEMENT,
          TrackingConstants.SAVE_SURVEY_CONFIG,
          TrackingConstants.FAILURE, 0);
        this.callSnackBarService(err, this.action);
      });
  }

  closeDialog(formResponse: Object): void {
    this.createSurveyConfigForm.reset();
    this.dialogRef.close(formResponse);
  }

  getSurveyNameValueObject(surveyTemplateId: number): SurveyTemplate {
    return this.surveyTemplates
      .find(surveyTemplate => surveyTemplate.id === surveyTemplateId);
  }

  getEntityTypeObject(entityTypeId: number): SurveyEntityType {
    return this.entityTypes[entityTypeId];
  }

  getEntityTypeIndex(entityType: SurveyEntityType): number {
    if (this.entityTypes) {
      return this.entityTypes.indexOf(entityType);
    } else if (entityType.toString() === 'CLIENT') {
      return 0;
    } else if (entityType.toString() === 'ORGANISATION') {
      return 1;
    } else if (entityType.toString() === 'PERSON') {
      return 2;
    }
  }

  callSnackBarService(response: any, operation: string) {
    this.snackBarService
      .showStatus(response,
      operation === 'Add'
      && MessageService.ADD_SURVEY_CONFIG_SUCCESS || MessageService.UPDATE_SURVEY_CONFIG_SUCCESS,
      MessageService.SURVEY_CONFIG_FAILURE);
  }

  updateSurveyConfigForm(): void {
    if (this.selectedConfiguration) {
      this.surveyId = this.selectedConfiguration.id;
      this.createSurveyConfigForm.patchValue({
        entityType: this.getEntityTypeIndex(this.selectedConfiguration.entityType),
        surveyName: this.selectedConfiguration.surveyTemplateVO.id,
        surveyTabTitle: this.selectedConfiguration.tabTitle,
        surveyLocation: this.selectedConfiguration.locationId,
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/survey/addsurveyconfig/add-surveyconfig.component.ts